<template>
  <div class="multi-selection-table-container no-select scroll-fire-fox">
    <slot name="header" />

    <v-data-table
      :headers="headers"
      :items="objects"
      :page.sync="pagination.page"
      :sort-by="pagination.sortBy"
      :sort-desc="pagination.descending"
      :items-per-page.sync="pagination.rowsPerPage"
      fixed-header
      disable-sort
      hide-default-header
      hide-default-footer
      class="custom multi-selection-table flex-grow-1"
    >
      <template v-slot:header>
        <thead>
          <tr class="table-header">
            <th class="object-index" :style="headerStyle" />
            <th :style="headerStyle">
              <span :style="headerLabelStyle">ID</span>
            </th>

            <table-header-cell
              v-for="header in headers"
              :key="header.label"
              :header="header"

              :pagination="pagination"
              :sort-icon="
                pagination.descending && header.value === pagination.sortBy
                  ? 'arrow_drop_up'
                  : 'arrow_drop_down'
              "
              :header-style="headerStyle"
              :header-label-style="headerLabelStyle"
              style="padding-left: 16px"
            ></table-header-cell>

            <!-- spacer for the technical site data -->
            <th v-if="layerHasTechnicalSite" :style="headerStyle">
              <span :style="headerLabelStyle">{{
                $tc("TechnicalSite", 1)
              }}</span>
            </th>
            <th
              v-for="technicalSiteFieldKey in technicalSiteKeys"
              :key="technicalSiteFieldKey"
              :style="headerStyle"
            >
              <span :style="headerLabelStyle">{{ technicalSiteFieldKey }}</span>
            </th>

            <!-- spacer for the node data -->
            <th v-if="layerHasNode" :style="headerStyle">
              <span :style="headerLabelStyle">{{ $tc("Node", 1) }}</span>
            </th>
            <th
              v-for="nodeFieldKey in nodeKeys"
              :key="nodeFieldKey"
              :style="headerStyle"
            >
              <span :style="headerLabelStyle">{{ nodeFieldKey }}</span>
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:item="props" v-if="!loadingObjects">
        <tr
          class="table-body"
          :class="{
            selected: selected.includes(props.index),
          }"
        >
          <td class="object-index" @click="select(props.index, $event)">
            {{ props.index + 1 }}
          </td>

          <td class="input-disabled" @click="select(props.index, $event)">
            {{ props.item[initials + "_id"] }}
          </td>

          <td
            v-for="(field, fieldIndex) in fields"
            :key="fieldIndex + '-' + field.var_name"
            :class="editionMode ? field.size : ''"
            :ref="getInputRef(props.index, fieldIndex)"
          >
            <div
              class="input-disabled with-select"
              v-if="
                (!editionMode || (editionMode && field.multi_select_disable && field.disable)) &&
                field.input_type !== 'checkbox'
              "
              @click="select(props.index, $event)"
            >
              {{ getNonEditableValue(props.item, field) }}
            </div>

            <input
              v-else-if="isStandardInput(field.input_type)"
              :type="getSpecificStandardInputType(field.input_type)"
              :value="getValue(props.item, field)"
              :min="0"
              @change="field.input_type === 'number' && $event.target.value < 0 ? setValue(props.index, field, 0) : setValue(props.index, field, $event.target.value)"
              class="table-input"
              :class="{
                edited: valueWasChanged(props.index, field),
                'required-not-filled': requiredWasMadeEmpty(props.index, field),
              }"
              @keydown.ctrl.up.prevent="
                changePositionFocus(props.index, fieldIndex, -1000)
              "
              @keydown.ctrl.down.prevent="
                changePositionFocus(props.index, fieldIndex, 1000)
              "
              @keydown.ctrl.left.prevent="
                changePositionFocus(props.index, fieldIndex, -1)
              "
              @keydown.ctrl.right.prevent="
                changePositionFocus(props.index, fieldIndex, 1)
              "
            />

            <treeselect
              v-else-if="
                isSelectInput(field.input_type) &&
                field.var_name === prefix + '_material_id'
              "
              :value="getValue(props.item, field)"
              @input="setValue(props.index, field, $event)"
              :options="materialOptions"
              :normalizer="materialOptionsNormalizer"
              :load-options="loadMaterialOptions"
              :clearable="selectIsClearable(field)"
              :disabled="true"
              class="multi-selection-select"
              :class="{
                edited: valueWasChanged(props.index, field),
                'required-not-filled': requiredWasMadeEmpty(props.index, field),
              }"
              disableFuzzyMatching
              @keydown.ctrl.up.prevent.native="
                changePositionFocus(props.index, fieldIndex, -1000)
              "
              @keydown.ctrl.down.prevent.native="
                changePositionFocus(props.index, fieldIndex, 1000)
              "
              @keydown.ctrl.left.prevent.native="
                changePositionFocus(props.index, fieldIndex, -1)
              "
              @keydown.ctrl.right.prevent.native="
                changePositionFocus(props.index, fieldIndex, 1)
              "
            />

            <treeselect
              v-else-if="isSelectInput(field.input_type)"
              :value="getValue(props.item, field)"
              @input="setValue(props.index, field, $event)"
              :options="
                getSpecificListOfOptions(field, getValue(props.item, field))
              "
              :clearable="selectIsClearable(field)"
              :disabled="field.multi_select_disable"
              class="multi-selection-select"
              :class="{
                edited: valueWasChanged(props.index, field),
                'required-not-filled': requiredWasMadeEmpty(props.index, field),
              }"
              disableFuzzyMatching
              @keydown.ctrl.up.prevent.native="
                changePositionFocus(props.index, fieldIndex, -1000)
              "
              @keydown.ctrl.down.prevent.native="
                changePositionFocus(props.index, fieldIndex, 1000)
              "
              @keydown.ctrl.left.prevent.native="
                changePositionFocus(props.index, fieldIndex, -1)
              "
              @keydown.ctrl.right.prevent.native="
                changePositionFocus(props.index, fieldIndex, 1)
              "
            />

            <treeselect
              v-else-if="field.input_type === 'user'"
              :value="getValue(props.item, field)"
              @input="setValue(props.index, field, $event)"
              :options="userOptions"
              :normalizer="userNormalizer"
              :load-options="loadUserOptions"
              :clearable="selectIsClearable(field)"
              :disabled="field.multi_select_disable"
              class="multi-selection-select"
              :class="{
                edited: valueWasChanged(props.index, field),
                'required-not-filled': requiredWasMadeEmpty(props.index, field),
              }"
              disableFuzzyMatching
              @keydown.ctrl.up.prevent.native="
                changePositionFocus(props.index, fieldIndex, -1000)
              "
              @keydown.ctrl.down.prevent.native="
                changePositionFocus(props.index, fieldIndex, 1000)
              "
              @keydown.ctrl.left.prevent.native="
                changePositionFocus(props.index, fieldIndex, -1)
              "
              @keydown.ctrl.right.prevent.native="
                changePositionFocus(props.index, fieldIndex, 1)
              "
            />

            <div
              v-if="field.input_type === 'checkbox'"
              class="multi-selection-checkbox-container"
              :class="{
                edited: valueWasChanged(props.index, field),
                'required-not-filled': requiredWasMadeEmpty(props.index, field)
              }"
            >
              <v-checkbox
                :input-value="valueCheckboxInObject(props.item, field)"
                @change="setValue(props.index, field, $event)"
                class="multi-selection-checkbox"
                style="z-index: 100"
                :color="valueWasChanged(props.index, field) ? '#FFF' : '#767676'"
                :ripple="false"
                :disabled="!editionMode"
                :hide-details="true"
                disableFuzzyMatching
                @keydown.ctrl.up.prevent.native="
                  changePositionFocus(props.index, fieldIndex, -1000)
                "
                @keydown.ctrl.down.prevent.native="
                  changePositionFocus(props.index, fieldIndex, 1000)
                "
                @keydown.ctrl.left.prevent.native="
                  changePositionFocus(props.index, fieldIndex, -1)
                "
                @keydown.ctrl.right.prevent.native="
                  changePositionFocus(props.index, fieldIndex, 1)
                "
              />
            </div>
          </td>

          <!-- spacer for the technical site data -->
          <td v-if="layerHasTechnicalSite" class="object-index" />
          <td
            class="input-disabled"
            v-for="technicalSiteFieldKey in technicalSiteKeys"
            :key="technicalSiteFieldKey"
          >
            {{ props.item.technicalsite[technicalSiteFieldKey] }}
          </td>

          <!-- spacer for the node data -->
          <td v-if="layerHasNode" class="object-index" />
          <td class="input-disabled" v-for="nodeKey in nodeKeys" :key="nodeKey">
            {{
              !!props.item.technicalsite
                ? props.item.technicalsite.node[nodeKey]
                : !!props.item.technicalpoint
                ? props.item.technicalpoint.node[nodeKey]
                : props.item.node[nodeKey]
            }}
          </td>
        </tr>
      </template>
    </v-data-table>

    <div
      v-if="!loading && fields.length > 0 && objects.length === 0"
      class="table-warning"
    >
      {{ $t("NoObjectsOfTypeInSelectedArea") }}
    </div>

    <multiselect-edit-selected-form
      v-if="editSelectedOpen"
      :dialog-is-open="editSelectedOpen"
      :fields="fields"
      :objects="objects"
      :selected="selected"
      :options="options"
      :optionsUser="userOptions"
      :materialOptions="materialOptions"
      :prefix="prefix"
      @close="closeEditSelected"
      @apply="applyMassEdition"
    >
    </multiselect-edit-selected-form>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="true"
      color="#29BDF0"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import _ from "lodash";
import { multiSelectionMixin } from "@/mixins/multiSelectionMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import TableHeaderCell from "@/components/FieldManagement/FieldWork/Data/ActivityListTableHeaderCell";
import LoadingSpinner from "vue-loading-overlay";
import MultiselectEditSelectedForm from "./MultiselectEditSelectedForm";

export default {
  name: "MultiselectTable",

  components: {
    TableHeaderCell,
    LoadingSpinner,
    MultiselectEditSelectedForm,
  },

  mixins: [multiSelectionMixin, checkPermissionsMixin],

  props: {
    layer: {
      type: String,
      required: true,
    },

    listsAreLoaded: {
      type: Boolean,
      required: true,
    },

    multiSelectData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      initials: null,
      fields: [],
      originalObjects: [],
      objects: [],
      selected: [],
      materialOptions: null,
      userOptions: null,
      minNumberHPsPerMDU: null,

      loadingStructure: true,
      loadingObjects: true,
      deletingObjects: false,

      pagination: {
        sortBy: "",
        descending: false,
        rowsPerPage: -1,
      },

      headerStyle: "min-width: max-content",
      headerLabelStyle: { overflow: "visible", color: "#767676 !important" },

      editionMode: false,
      onlyUsedElements: false,
      showObjectDetails: false,
      editSelectedOpen: false,

      dataToSend: [],
      closuresDelete: [],

      lastItemClicked: undefined,
    };
  },

  mounted() {
    this.receiveInstructionsFromToolbar();
    this.UPDATE_MULTI_OBJECT_SELECTION(this.multiSelectData);
    this.getNetworkRuleMDU();
  },

  destroyed() {
    bus.$off("multiple-selection-update-mode");
    bus.$off("multiple-selection-toggle-elements-in-use");
    bus.$off("multiple-selection-activate-edit-selected");
    bus.$off("multiple-selection-save");
    bus.$off("multiple-selection-delete");
    // bus.$off("multiple-selection-clone");
    bus.$off("multiple-selection-select-all");
    bus.$off("multiple-selection-invert-selection");
    bus.$off("multiple-selection-clear-selection");
    bus.$off("multiple-selection-refresh");
    bus.$off("multiple-selection-delete-closure");
    bus.$off("multiple-selection-get-data-to-export");
    bus.$off("multiple-selection-toggle-details");
  },

  computed: {
    /**
     * Determines the endpoint to use to make any requests.
     */
    endpoint() {
      return this.$store.getters.getAllObjectTypesProperties.length > 0
        ? this.$store.getters.getLayerEndpoint(this.layer)
        : null;
    },

    /**
     * Determines the prefix of the current.
     */
    prefix() {
      return this.$store.getters.getLayerInitials(this.layer);
    },

    /**
     * Determines if the datatable content is loading or not.
     * @return {Boolean}
     */
    loading() {
      return (
        this.loadingStructure || this.loadingObjects || this.deletingObjects
      );
    },

    /**
     * Determines the number of objects
     * @return {Number}
     */
    numberObjects() {
      return this.objects.length;
    },

    /**
     * Determines the number of objects selected
     * @return {Number}
     */
    numberSelectedObjects() {
      return this.selected.length;
    },

    /**
     * Determines if any objects have been edited or not.
     */
    objectsWereEdited() {
      return this.dataToSend.length > 0;
    },

    /**
     * Determines the number of fields
     * @return {Number}
     */
    numberFields() {
      return this.fields.length;
    },

    dataToExportHeaders() {
      let headers = { id: "ID" };
      for (const field of this.fields) {
        headers[field.var_name] = field.multi_select_label
          ? field.multi_select_label
          : field.label;
      }

      if (typeof this.objects[0].coordinates === "object") {
        headers.latitude = "Latitude";
        headers.longitude = "Longitude";
      }
      return headers;
    },

    headers() {
      let headers = [];
      this.fields.forEach((field) => {
        headers.push({
          text: field.label,
          align: "left",
          value: field.var_name,
          hasFilter: false,
          width: "auto",
        });
      });
      return headers;
    },

    getFieldsToInclude() {
      switch (this.endpoint) {
        case "closures":
          return "technicalpoint,fiberterminalbox,images,node,accessories";
        case "technicalpoints":
          return "images,accessories";
        case "buildings":
          return "images,technicalsite,technicallocals,customer_premises,buildingColumns.floorboxes";
        case "sites":
        case "technicallocals":
          return "technicalsite";
        case "technicalsites":
          return "technicallocals,images,accessories";
        case "cables":
          return "conducts,accessories";
        case "slacks":
          return "cable";
        case "annotationspoints":
          return "images";
        case "pathways":
          return "accessories";
        default:
          return "";
      }
    },

    technicalSiteKeys() {
      return this.layerHasTechnicalSite
        ? Object.keys(this.objects[0].technicalsite).filter((key) =>
            key.includes("ts_")
          )
        : [];
    },

    layerHasTechnicalSite() {
      return this.objects.length > 0 && !!this.objects[0].technicalsite;
    },

    nodeKeys() {
      return this.layerHasNode
        ? !!this.objects[0].technicalsite &&
          !!this.objects[0].technicalsite.node
          ? Object.keys(this.objects[0].technicalsite.node).filter((key) =>
              key.includes("nd_")
            )
          : !!this.objects[0].technicalpoint &&
            !!this.objects[0].technicalpoint.node
          ? Object.keys(this.objects[0].technicalpoint.node).filter((key) =>
              key.includes("nd_")
            )
          : Object.keys(this.objects[0].node).filter((key) =>
              key.includes("nd_")
            )
        : [];
    },

    layerHasNode() {
      return (
        this.objects.length > 0 &&
        ((!!this.objects[0].technicalsite &&
          !!this.objects[0].technicalsite.node) ||
          (!!this.objects[0].technicalpoint &&
            !!this.objects[0].technicalpoint.node) ||
          !!this.objects[0].node)
      );
    },

    detailsOptions() {
      return [
        {
          label: "CustomData",
          show: this.objects.length > 0 && !!this.objects[0].custom_data,
          prefix: "",
        },
        {
          label: "TechnicalSite",
          show: this.objects.length > 0 && !!this.objects[0].technicalsite,
          prefix: "ts_",
        },
        {
          label: "TechnicalPoint",
          show: this.objects.length > 0 && !!this.objects[0].tecnhicalpoint,
          prefix: "tp_",
        },
        {
          label: "Node",
          show:
            this.objects.length > 0 &&
            ((!!this.objects[0].technicalsite &&
              !!this.objects[0].technicalsite.node) ||
              !!this.objects[0].node),
          prefix: "nd_",
        },
      ];
    },

    layerMaterialFamilyCode() {
      switch (this.layer) {
        case "fc_cable":
          return "ca";
        case "fc_closure":
          return "cl";
        case "fc_pole":
          return "pl";
        case "fc_manhole":
          return "ch";
        case "fc_cabinet":
          return "cb";
        case "fc_toby_box":
          return "tb";
        case "fc_pathway":
          return "pt";
        default:
          return null;
      }
    },
  },

  watch: {
    buildingType(newValue) {
      let field = this.fields.find(
        (field) =>
          field.var_name === "bd_type" &&
          field.multi_select_label === "Building Type"
      );
      this.updateValue(field, newValue);
    },
    layer() {
      if (this.listsAreLoaded) {
        this.updateTable();
      }
    },

    /**
     * Sends an update to the toolbar regarding the total number of objects.
     * @param {Number} newValue - new total of objects
     */
    numberObjects(newValue) {
      bus.$emit("multiple-selection-update-number-objects", newValue);
    },

    /**
     * Sends an update to the toolbar regarding the number of select objects.
     * @param {Number} newValue - new number of selected objects
     */
    numberSelectedObjects(newValue) {
      bus.$emit("multiple-selection-update-number-selected-objects", newValue);
    },

    /**
     * Sends an update to the toolbar regarding the presence of changed objects.
     * @param {Boolean} newValue - if there are edited objects or not
     */
    objectsWereEdited(newValue) {
      bus.$emit("multiple-selection-update-edition-state", newValue);
      bus.$emit(
        "multiple-selection-update-save-button-state",
        !(newValue && this.editionMode)
      );
    },

    // loading() {
    //   this.$emit("loadingObjects", this.loading);
    // },

    listsAreLoaded() {
      if (this.endpoint) {
        this.updateTable();
      }
    },
  },

  methods: {
    ...mapActions([
      "FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE",
      "GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON",
      "UPDATE_MULTI_OBJECT_SELECTION",
      "DELETE_OBJECTS",
      "EDIT_OBJECTS",
      "fetchAllUsers",
      "fetchStockMaterialsList",
      "FETCH_ALL_NETWORK_RULES_TEMPLATES,"
    ]),

    buildingType(object) {
      let totalHPs = 0;
      if (object.bd_demand_point_hab_bals) {
        totalHPs += Number(object.bd_demand_point_hab_bals);
      }
      if (object.bd_demand_point_com_bals) {
        totalHPs += Number(object.bd_demand_point_com_bals);
      }
      if (object.bd_demand_point_pub_bals) {
        totalHPs += Number(object.bd_demand_point_pub_bals);
      }
      if (object.bd_demand_point_emp_bals) {
        totalHPs += Number(object.bd_demand_point_emp_bals);
      }
      if (totalHPs >= this.minNumberHPsPerMDU) {
        return "MDU";
      } else if (totalHPs < this.minNumberHPsPerMDU && totalHPs >= 0) {
        return "SDU";
      }
    },

    /**
     * Updates the table structure and content.
     */
    updateTable() {
      this.clearDataToSend();
      this.getTableStructure();
      if (this.endpoint) {
        this.getObjects();
      }
    },

    /**
     * Retrieves the current tab object type from the store.
     */
    async getTableStructure() {
      this.fields = [];
      this.loadingStructure = true;
      bus.$emit("change-multi-selection-toolbar-state", false);
      try {
        let data = await this.FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE({
          layer: this.layer,
          needsTabs: false,
          needsFields: true,
        });
        let formFields = data.fields;
        this.initials = data.initials;
        formFields.forEach((field, index) => {
          if (
            field.hasOwnProperty("multi_select_show") &&
            field.hasOwnProperty("multi_select_label") &&
            field.multi_select_show &&
            !field.hide
          ) {
            field.label = field.multi_select_label;
            if (field.input_type === "checkbox") {
              this.getCheckboxFields(field);
            } else {
              if (
                field.input_type === "select" &&
                field.hasOwnProperty("select_list_name")
              ) {
                this.getSelectOptions(field.select_list_name);
              } else if (field.input_type === "radio") {
                this.getRadioOptions(field);
              }

              this.fields.push(field);
            }
          }
        });
        let labelIndex = this.fields
          .map((field) => field.label)
          .indexOf("Label");
        if (labelIndex > -1) {
          this.fields.splice(labelIndex + 1, 0, {
            id: "labelIndex",
            input_type: "checkbox",
            label: "Label Lock",
            required: false,
            multi_select_disable: false,
            var_name: this.initials + "_label_locked",
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingStructure = false;
        this.loadMaterialOptions();
        bus.$emit("change-multi-selection-toolbar-state", true);
      }
    },

    /**
     * Obtains the objects of the currently selected layer present in the selected area.
     */
    async getObjects() {
      this.selected = [];
      this.originalObjects = [];
      this.objects = [];
      this.loadingObjects = true;
      let technicalType = undefined;
      if (this.endpoint.includes("technical")) {
        technicalType = this.layer;
      }
      try {
        let dataToSend = {
          endpoint: this.endpoint,
          technicalType: technicalType,
          usedByNetwork: this.onlyUsedElements,
          fieldsToAppend: "coordinates",
        };
        if (this.showObjectDetails) {
          dataToSend.fieldsToInclude = this.getFieldsToInclude;
        }
        let objects = await this.GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON(
          dataToSend
        );
        let filteredObjects = this.filterObjects(
          objects,
          this.layer.includes("base")
        );
        this.originalObjects = JSON.parse(JSON.stringify(filteredObjects));
        this.objects = filteredObjects;
        this.$emit("setObjectCount", this.objects.length);
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingObjects = false;
      }
    },

    /**
     * Gets only the objects corresponding to the given layer.
     * @param {Array} objectList - list of objects returned by the server
     * @param {Boolean} addIfBlocked - determines if it's the blocked or unblocked objects that are to be added to
     * the current list of objects.
     * @returns {Array}
     */
    filterObjects(objectList, addIfBlocked) {
      let filteredObjects = [];
      let blockedVar = this.initials + "_blocked";
      if (objectList.length > 0 && objectList[0].hasOwnProperty(blockedVar)) {
        objectList.forEach((object) => {
          if (
            (addIfBlocked && object[blockedVar]) ||
            (!addIfBlocked && !object[blockedVar])
          ) {
            filteredObjects.push(object);
          }
        });
      } else {
        filteredObjects = objectList;
      }
      return filteredObjects;
    },

    /**
     * Objects the UUID value of a given object
     * @param {Object} object
     * @returns {String}
     */
    getObjectUUID(object) {
      return object[this.initials + "_uuid"];
    },

    /**
     * Opens the necessary channels to receive instructions from the toolbar.
     */
    receiveInstructionsFromToolbar() {
      bus.$on("multiple-selection-update-mode", (isActive) =>
        this.updateMode(isActive)
      );
      bus.$on("multiple-selection-toggle-elements-in-use", (isActive) =>
        this.toggleElementsInUse(isActive)
      );
      bus.$on("multiple-selection-activate-edit-selected", () => {
        this.editSelectedOpen = true;
      });
      bus.$on("multiple-selection-save", () => this.save());
      bus.$on("multiple-selection-delete", () =>
        this.remove(this.getSelectedUUIDs())
      );
      // bus.$on("multiple-selection-clone", () => this.clone());
      bus.$on("multiple-selection-select-all", () => this.selectAll());
      bus.$on("multiple-selection-invert-selection", () =>
        this.invertSelection()
      );
      bus.$on("multiple-selection-clear-selection", () =>
        this.clearSelection()
      );
      bus.$on("multiple-selection-refresh", () => this.refresh());
      bus.$on("multiple-selection-delete-closure", () => this.deleteClosures());
      bus.$on("multiple-selection-get-data-to-export", () =>
        this.getDataToExport()
      );
      bus.$on("multiple-selection-toggle-details", (isActive) =>
        this.toggleDetails(isActive)
      );
    },

    /**
     * Determines if the user if making a simple select or a bulk select and executes the correct action.
     * @param {Number} index - index of the object clicked
     * @param {Object} e - information about the click event
     */
    select(index, e) {
      if (e.shiftKey) {
        this.bulkSelect(index);
      } else {
        this.toggleObject(index);
      }
      this.lastItemClicked = index;
    },

    /**
     * Allows selecting a group of items in bulk.
     * @param index - index of the last clicked item.
     */
    bulkSelect(index) {
      let previousClick = 0;
      let firstItem;
      let lastItem;
      if (this.lastItemClicked !== undefined) {
        previousClick = this.lastItemClicked;
      }
      if (previousClick === index) {
        this.toggleObject(index);
      } else {
        if (previousClick < index) {
          firstItem = previousClick;
          lastItem = index;
        } else if (previousClick > index) {
          firstItem = index;
          lastItem = previousClick;
        }
        for (let i = firstItem; i <= lastItem; i++) {
          if (!this.selected.includes(i)) {
            this.selected.push(i);
          }
        }
      }
    },

    /**
     * Adds or removes an object index from the selected list
     * @param {number} index - index of the object in the objects list
     */
    toggleObject(index) {
      let selectedIndex = this.selected.findIndex(
        (selected) => selected === index
      );
      if (selectedIndex !== -1) {
        this.selected.splice(selectedIndex, 1);
      } else {
        this.selected.push(index);
      }
    },

    getGeoJson(index) {
      let geoJson;
      let object = this.objects[index];
      if (this.endpoint === "technicalpoints") {
        geoJson = object.node.nd_geo_json;
      } else {
        geoJson = object[this.initials + "_geo_json"];
      }
      return geoJson;
    },

    getUuid(index) {
      let object = this.objects[index];
      return object[this.initials + "_uuid"];
    },

    /**
     * Adds all objects to the selected list.
     */
    selectAll() {
      this.clearSelection();
      let nrOfObjects = this.objects.length;
      for (let i = 0; i < nrOfObjects; i++) {
        this.selected.push(i);
      }
    },

    /**
     * Inverts the selection, i.e. adds the previously non-selected objects to the list and removes the previously
     * selected from the list.
     */
    invertSelection() {
      let nrOfObjects = this.objects.length;
      for (let i = 0; i < nrOfObjects; i++) {
        this.toggleObject(i);
      }
    },

    /**
     * Removes all objects from the selected list.
     */
    clearSelection() {
      this.selected = [];
    },

    /**
     * Creates a list of the UUIDs of the selected objects.
     */
    getSelectedUUIDs() {
      let deleteRequestData = [];
      if (this.endpoint === "technicalpoints") {
        deleteRequestData = {
          uuids: [],
          extra_data: {
            join_pathways: "true",
          },
        };

        this.selected.forEach((objectIndex) => {
          deleteRequestData.uuids.push(
            this.getObjectUUID(this.objects[objectIndex])
          );
        });
      } else {
        this.selected.forEach((objectIndex) => {
          deleteRequestData.push({
            uuid: this.getObjectUUID(this.objects[objectIndex]),
          });
        });
      }

      return deleteRequestData;
    },

    /**
     * Eliminates the selected objects from the database.
     */
    remove(uuidOfObjects) {
      this.deletingObjects = true;
      this.DELETE_OBJECTS({
        endpoint: this.endpoint,
        objects: uuidOfObjects,
      })
        .then(() => {
          if (this.endpoint === "closures") {
            this.closuresDelete = {
              dialogOpen: false,
              associatedCables: null,
            };
          }

          this.refresh();
        })
        .catch((error) => {
          if (error.response.data.status_code === 423) {
            this.$toasted.global.toast_error(
              this.$t("DeletePoleWithAssociatedClosure")
            );
          } else {
            console.error(error);
          }

          if (this.endpoint === "closures") {
            this.closuresDelete = {
              dialogOpen: true,
              associatedCables: error.response.data.data,
              selected: this.selected,
            };
          }
        })
        .then(() => {
          this.$nextTick(() =>
            this.$emit("closureDelete", this.closuresDelete)
          );
          this.deletingObjects = false;
        });
    },

    deleteClosures() {
      let uuidOfObjectsSelected = this.getSelectedUUIDs();
      let uuidOfObjectsDeleted = [];

      uuidOfObjectsSelected.forEach((uuidObject) => {
        let objectIsRemove = false;
        if (this.closuresDelete && this.closuresDelete.associatedCables) {
          Object.keys(this.closuresDelete.associatedCables).forEach(
            (uuidClosure) => {
              if (uuidClosure === uuidObject.uuid) {
                objectIsRemove = true;
              }
            }
          );
        }
        if (!objectIsRemove) {
          uuidOfObjectsDeleted.push(uuidObject);
        }
      });
      if (uuidOfObjectsDeleted.length > 0) {
        this.remove(uuidOfObjectsDeleted);
      } else {
        this.closuresDelete = {
          dialogOpen: false,
          associatedCables: null,
          selected: null,
        };
        this.$emit("closureDelete", this.closuresDelete);
      }
    },

    /**
     * Updates the state of the edition mode.
     * @param {Boolean} isActive - if the edition mode is active or not
     */
    updateMode(isActive) {
      if (this.objectsWereEdited) {
        this.clearDataToSend();
        this.refresh();
      }
      this.editionMode = isActive;
    },

    /**
     * Get the field value in non edition mode.
     * @param {Object} object
     * @param {Object} field
     * @returns {Number | String}
     */
    getNonEditableValue(object, field) {
      let value = this.getValue(object, field);
      if (value === null || value === undefined || value === "") {
        value = "-";
      } else if (
        field.input_type === "select" &&
        field.hasOwnProperty("select_list_name") &&
        field.select_list_name
      ) {
        value = this.getOptionLabel(field.select_list_name, value);
      } else if (
        field.input_type === "select" &&
        field.var_name === this.prefix + "_material_id"
      ) {
        let material = this.materialOptions.find(
          (option) => option.sm_id === object[field.var_name]
        );
        if (material) {
          value = this.materialOptionsNormalizer(material).label;
        }
      } else if (
        field.input_type === "radio" &&
        field.hasOwnProperty("var_name") &&
        field.var_name
      ) {
        value = this.getOptionLabel(field.var_name, value);
      }
      return value;
    },

    /**
     * Gets the label corresponding to the value of a given select variable
     * @param {Object} optionList - list being analysed
     * @param id
     * @return {String | Number}
     */
    getOptionLabel(optionList, id) {
      if (this.options[optionList] !== undefined) {
        let option = this.options[optionList].find((item) => item.id == id);
        if (option !== undefined) {
          return option.label;
        } else return id;
      } else {
        return id;
      }
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getCheckboxFields(field) {
      if (field.hasOwnProperty("checkbox_options")) {
        for (const option of field.checkbox_options) {
          let checkboxField = option;
          checkboxField.input_type = field.input_type;
          this.fields.push(checkboxField);
        }
      }
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getRadioOptions(field) {
      if (field.hasOwnProperty("var_name") && field.var_name) {
        let varName = field.var_name;
        this.options[varName] = [];
        if (field.hasOwnProperty("radio_options")) {
          for (const option of field.radio_options) {
            let radioOption = {
              id: option.value,
              label: option.label,
            };
            this.options[varName].push(radioOption);
          }
        }
      }
    },

    /**
     * Obtains the options for a given select list.
     * @param {String} listName - name of the list to use
     */
    getSelectOptions(listName) {
      if (
        !this.options.hasOwnProperty(listName) ||
        this.options[listName].length === 0
      ) {
        let list = this.$store.getters.getListOfOptionsByListName(
          listName,
          [],
          true
        );

        if (list) {
          this.options[listName] = [];
          for (const option of list) {
            this.options[listName].push(this.normalizer(option));
          }
        }
      }
    },

    loadUserOptions({ callback }) {
      this.fetchAllUsers()
        .then((resp) => {
          this.userOptions = resp.data.data;
        })
        .catch((error) => {
          console.error(error);
        })
        .then(() => {
          callback();
        });
    },

    /**
     * Normalizes the key-value pairs of each given list option.
     * @param {Object} node - option being normalized.
     * @returns {Object}
     */
    normalizer(node) {
      let id = "";
      let label = "";
      switch (true) {
        case node.hasOwnProperty("code"):
          id = node.code;
          if (node.hasOwnProperty("client_label") && node.client_label) {
            label = node.client_label;
          } else if (node.hasOwnProperty("libelle") && node.libelle) {
            label = node.libelle;
          } else if (node.hasOwnProperty("reference") && node.reference) {
            //cableMaterialList
            label = node.reference;
          } else if (node.hasOwnProperty("name") && node.name) {
            //cableMaterialList
            label = node.name;
          } else if (
            node.hasOwnProperty("municipality_label") &&
            node.municipality_label
          ) {
            label = node.municipality_label;
          }
          break;
        case node.hasOwnProperty("or_id"): //OwnerList
          id = node.or_id;
          label = node.or_nom;
          break;
        case node.hasOwnProperty("pw_id"): //PathwayIdsList
          id = node.pw_id;
          label = node.pw_id;
          break;
      }
      return {
        id: id,
        label: label,
        isDisabled: node.deleted,
      };
    },

    /**
     * Checks if the value of a field is different from the original one.
     * @param {Number} objectIndex - index of the edited object
     * @param {Object} field - field being edited
     * @returns {boolean}
     */
    valueWasChanged(objectIndex, field) {
      let originalValue = null;
      let newValue = null;
      if (field.var_name.includes('extra_fields')) {
        let extraFieldVarName = field.var_name.split('.')[1];
        if (this.originalObjects[objectIndex].extra_fields !== null) {
          originalValue = this.originalObjects[objectIndex].extra_fields[extraFieldVarName];
          newValue = this.objects[objectIndex].extra_fields[extraFieldVarName];
        }
      } else {
        originalValue = this.originalObjects[objectIndex][field.var_name];
        newValue = this.objects[objectIndex][field.var_name];
      }
      return originalValue !== newValue;
    },

    /**
     * Checks if the value of a required field was removed.
     * @param {Number} objectIndex - index of the edited object
     * @param {Object} field - field being edited
     * @returns {boolean}
     */
    requiredWasMadeEmpty(objectIndex, field) {
      let extraFieldVarName = field.var_name.includes('extra_fields') ? field.var_name.split('.')[1] : null;
      return extraFieldVarName
        ? field.required && !this.objects[objectIndex].extra_fields[extraFieldVarName]
        : field.required && !this.objects[objectIndex][field.var_name];
    },

    /**
     * Gets the original value of a given variable
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {Object} field - field being analysed
     * @param value
     * @return {String | Number}
     */
    setValue(objectIndex, field, value) {
      let object = this.objects[objectIndex];
      _.set(object, field.var_name, value);
      this.updateDataToSend(objectIndex, field.var_name, value);
      if (
        field.var_name === "bd_demand_point_hab_bals" ||
        field.var_name === "bd_demand_point_com_bals" ||
        field.var_name === "bd_demand_point_pub_bals" ||
        field.var_name === "bd_demand_point_emp_bals"
      ) {
        let buildingType = this.buildingType(object);
        _.set(object, "bd_type", buildingType);
        this.updateDataToSend(objectIndex, "bd_type", buildingType);
      }
    },

    /**
     * Places the new value in an array to send to the server.
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {String} varName
     * @param {String | Number} newValue
     */
    updateDataToSend(objectIndex, varName, newValue) {
      let uuid = this.getObjectUUID(this.objects[objectIndex]);
      let newVarName = this.getDataToSendVarName(varName);
      let editedObjectIndex = this.dataToSend.findIndex(
        (editedObject) => editedObject.uuid === uuid
      );
      if (editedObjectIndex > -1) {
        this.setValueForDataToSend(editedObjectIndex, newVarName, newValue);
        _.set(this.dataToSend[editedObjectIndex], newVarName, newValue);
      } else {
        editedObjectIndex = this.dataToSend.length;
        this.dataToSend.push({ uuid: uuid });
        this.setValueForDataToSend(editedObjectIndex, newVarName, newValue);
      }
    },

    setValueForDataToSend(index, varName, newValue) {
      _.set(this.dataToSend[index], varName, newValue);
    },

    getDataToSendVarName(varName) {
      let updatedVarName = varName;
      if (varName.startsWith(this.initials)) {
        updatedVarName = varName.replace(this.initials + "_", "");
      } else if (varName.startsWith("address.ad_")) {
        updatedVarName = varName.replace("address.ad_", "address.");
      }
      return updatedVarName;
    },

    closeEditSelected() {
      this.editSelectedOpen = false;
      bus.$emit("multiple-selection-deactivate-edit-selected");
    },

    /**
     * Applies the new values to all the selected objects
     * @param {Object} newValues
     */
    applyMassEdition(newValues) {
      this.closeEditSelected();
      this.selected.forEach((objectIndex) => {
        for (let key in newValues) {
          if (key === 'extra_fields') {
            let currentValue = this.objects[objectIndex][key];
            this.objects[objectIndex][key] = { ...currentValue, ...newValues[key] };
            this.updateDataToSend(objectIndex, key, { ...currentValue, ...newValues[key] })
          } else {
            this.objects[objectIndex][key] = newValues[key];
            this.updateDataToSend(objectIndex, key, newValues[key])
          }
        }
      });
    },

    /**
     * Saves the data that was changed.
     */
    save() {
      if (this.dataToSend.length !== 0) {
        this.EDIT_OBJECTS({
          endpoint: this.endpoint,
          data: this.dataToSend,
        })

          .then(() => this.updateTable())
          .catch((error) => console.error(error));
      } else this.updateTable();
      this.updateMode(false);
    },

    /**
     * Refresh the data shown.
     */
    refresh() {
      if (this.fields.length > 0) {
        this.getObjects();
      }
    },

    getDataToExport() {
      let dataToExport = this.getDataToExportMainContent();
      dataToExport.unshift(this.dataToExportHeaders);
      bus.$emit("multiple-selection-set-data-to-export", dataToExport);
    },

    getDataToExportMainContent() {
      let items = [];
      for (const objectIndex of this.selected) {
        const preparedObject = this.prepareObjectForExportation(objectIndex);
        items.push(preparedObject);
      }
      return items;
    },

    prepareObjectForExportation(objectIndex) {
      const object = this.objects[objectIndex];

      let preparedObject = { id: object[this.prefix + "_id"] };

      for (const field of this.fields) {
        let value = this.getValue(object, field);
        if (field.input_type === "checkbox") {
          value = !!value;
        } else if (field.input_type === "select" && value) {
          if (field.var_name === this.prefix + "_material_id") {
            value = this.materialOptions.find((option) => option.id === value);
          } else {
            let options = this.getSpecificListOfOptions(field);
            if (options) {
              let item = options.find((option) => option.id === value);
              if (item) {
                value = item.client_label
                  ? item.client_label
                  : item.label
                  ? item.label
                  : item.libelle;
              }
            }
          }
        } else {
          value = value === undefined || value === null ? "" : value;
        }
        preparedObject[field.var_name] = value;
      }

      if (typeof object.coordinates === "object") {
        preparedObject.latitude = object.coordinates.lat;
        preparedObject.longitude = object.coordinates.long;
      }

      return preparedObject;
    },

    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    /**
     * Clear the data to send object.
     */
    clearDataToSend() {
      this.dataToSend = [];
    },

    getInputRef(objectIndex, fieldIndex) {
      return objectIndex * 1000 + fieldIndex;
    },

    changePositionFocus(objectIndex, fieldIndex, valueChange) {
      let ref = this.getInputRef(objectIndex, fieldIndex);
      let nextRef = ref + valueChange;
      let nextFieldIndex = nextRef % 1000;
      let nextObjectIndex = (nextRef - nextFieldIndex) / 1000;

      if (
        nextObjectIndex >= 0 &&
        nextObjectIndex < this.numberObjects &&
        nextFieldIndex >= 0 &&
        nextFieldIndex < this.numberFields
      ) {
        let input = this.$refs[nextRef][0].querySelector("input");
        if (input === null || input.disabled) {
          nextRef = nextRef + valueChange;
          this.changePositionFocus(
            nextObjectIndex,
            nextFieldIndex,
            valueChange
          );
        } else {
          input.focus();
        }
      }
    },

    /**
     * Updates the state of the elements in use.
     * @param {Boolean} isActive - if it is to show only elements in use
     */
    toggleElementsInUse(isActive) {
      this.onlyUsedElements = isActive;
      this.clearDataToSend();
      this.refresh();
    },

    /**
     * Updates the state of the edition mode.
     * @param {Boolean} isActive - if the edition mode is active or not
     */
    toggleDetails(isActive) {
      this.showObjectDetails = isActive;
      this.clearDataToSend();
      this.refresh();
    },

    ///////////////////////////////
    /* Stock Material methods */
    ///////////////////////////////

    /**
     * Get all material options
     * @returns {Promise<void>}
     */
    async loadMaterialOptions() {
      try {
        let queryParameters = {
          material_family_id: this.layerMaterialFamilyCode,
        };
        if (queryParameters.material_family_id) {
          let result = await this.fetchStockMaterialsList(queryParameters);
          let options = _.cloneDeep(result);
          options.sort((a, b) => {
            let nameA = a.sm_short_description
              ? a.sm_short_description
              : a.sm_description;
            let nameB = b.sm_short_description
              ? b.sm_short_description
              : b.sm_description;
            nameA = JSON.stringify(nameA).toLowerCase();
            nameB = JSON.stringify(nameB).toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
          });
          this.materialOptions = options;
        }
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Normalizer for the list of materials options (so that the treeselect component can read it).
     * @param {object} node - Original material object
     * @return {object} the adapted object
     */
    materialOptionsNormalizer(node) {
      let label = node.sm_description;
      if (node.sm_short_description) {
        label = node.sm_short_description;
      }
      return {
        id: node.sm_id,
        label: label,
      };
    },

    /**
     * @returns - minNumberHPsPerMDU from network Rules
     */
    async getNetworkRuleMDU() {
      try {
        let workspace = await this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
          type: "id",
          value: sessionStorage.getItem("workspace_rules_id")
        });
        this.minNumberHPsPerMDU = workspace.data[0].data.mdu_classifier;
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style scoped>
.header {
  min-height: 80px;
  height: 80px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

th {
  position: sticky;
  top: 0;
  background-color: #67a0b426 !important;
  z-index: 1;
}

thead {
  position: sticky;
  top: 0;
  z-index: 150;
}

.multi-selection-table {
  position: relative;
  overflow: auto;
}

.custom.multi-selection-table >>> .v-data-table__wrapper table {
  table-layout: auto !important;
  position: absolute;
}

.multi-selection-table-container {
  position: absolute;
  overflow: hidden;
  left: 25px;
}

.with-select {
  user-select: text;
}

input,
.multi-selection-select >>> .vue-treeselect__control {
  border-radius: 8px;
}

input.edited,
.multi-selection-select.edited >>> .vue-treeselect__control,
.multi-selection-select.edited >>> .vue-treeselect__control .vue-treeselect__single-value {
  color: white;
}

.multi-selection-select {
  z-index: unset !important;
}

.multi-selection-checkbox-container {
  padding: 6px 0;
  border-radius: 8px;
  height: 32px;
  display: flex;
  padding-left: 0px;
  width: 32px;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Multiselect/MultiselectTable.vue